import { useEffect } from 'react'

// Constants
import { localStorageKeys } from '@sketch/constants'

// Utils
import { getParsedItem } from '@sketch/utils'

// Hooks
import { useModalContext } from 'containers/ModalManager/ModalContext'

// Components
import WorkspaceWelcomeModal from 'modules/workspace/modals/WorkspaceWelcomeModal'

// Types
import {
  WorkspaceMinimalFragment,
  useGetPaymentDetailsQuery,
  BillingMinimalFragment,
} from '@sketch/gql-types'

/**
 * Renders a Welcome Modal for Workspaces
 * Allows the user to subscribe right away or keep
 * using the Workspace as a Trial.
 *
 * Will only render if:
 *  - User is Owner of the Workspace
 *  - Was not dismissed before
 *  - Workspace is on trial
 *  - Workspace is not subscribed
 */
const useWelcomeModal = (
  workspace: WorkspaceMinimalFragment,
  subscriptionDetails: Pick<BillingMinimalFragment, 'status'>
) => {
  const { showModal } = useModalContext()

  const { data } = useGetPaymentDetailsQuery({
    variables: { customerId: workspace.customer?.identifier! },
    skip: !workspace.customer?.identifier,
  })

  const isSubscribed = data?.customer.paymentDetails.type !== 'NONE'
  const isTrial = subscriptionDetails.status === 'TRIALING'

  const wasWelcomeModalDismissed = getParsedItem(
    localStorageKeys.dismissedWelcomeModal
  )?.includes(workspace.identifier)

  const shouldRenderWelcomeModal =
    workspace.userIsOwner &&
    !wasWelcomeModalDismissed &&
    isTrial &&
    !isSubscribed

  /** Only render the modal according to the conditions above*/
  useEffect(() => {
    if (shouldRenderWelcomeModal) {
      showModal(WorkspaceWelcomeModal, { workspaceId: workspace.identifier })
    }
  }, [showModal, shouldRenderWelcomeModal, workspace])
}

export default useWelcomeModal
